// src/components/PatientActivityPanel.tsx
import React, { useState, useEffect, useCallback } from 'react';
import RecordingScreen from './RecordingScreen';

interface Activity {
  timestamp: string;
  mrn: string;
  activity_text: string;
  activity_type: 'text' | 'audio';
  logged_by?: string;
}

interface PatientActivityPanelProps {
  patientMRN: string;
  patientName: string;
  onBack: () => void;
  loggedInUserName: string; // Full name of logged-in user (e.g., "Kris Ghimire")
  onActivitySaved?: (type: 'text' | 'audio', activityText: string) => void; // Callback when activity is saved
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";


// Nurse-friendly illustration SVG component
const NurseClipboardIllustration: React.FC = () => (
  <svg 
    className="empty-illustration" 
    width="120" 
    height="120" 
    viewBox="0 0 120 120" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Clipboard background */}
    <rect x="25" y="20" width="70" height="90" rx="6" fill="#E2E8F0" stroke="#CBD5E1" strokeWidth="2"/>
    {/* Clipboard clip */}
    <rect x="40" y="12" width="40" height="16" rx="4" fill="#94A3B8"/>
    <circle cx="60" cy="20" r="4" fill="#64748B"/>
    {/* Lines on clipboard */}
    <line x1="35" y1="45" x2="85" y2="45" stroke="#CBD5E1" strokeWidth="3" strokeLinecap="round"/>
    <line x1="35" y1="60" x2="75" y2="60" stroke="#CBD5E1" strokeWidth="3" strokeLinecap="round"/>
    <line x1="35" y1="75" x2="80" y2="75" stroke="#CBD5E1" strokeWidth="3" strokeLinecap="round"/>
    <line x1="35" y1="90" x2="65" y2="90" stroke="#CBD5E1" strokeWidth="3" strokeLinecap="round"/>
    {/* Pencil */}
    <g transform="rotate(-30 85 85)">
      <rect x="80" y="55" width="8" height="40" rx="1" fill="#F59E0B"/>
      <polygon points="80,95 84,105 88,95" fill="#FCD34D"/>
      <rect x="80" y="55" width="8" height="6" fill="#DC2626"/>
    </g>
    {/* Plus sign for "add" */}
    <circle cx="90" cy="30" r="12" fill="#3B82F6" opacity="0.9"/>
    <line x1="90" y1="24" x2="90" y2="36" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="84" y1="30" x2="96" y2="30" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);

const PatientActivityPanel: React.FC<PatientActivityPanelProps> = ({ patientMRN, patientName, onBack, loggedInUserName, onActivitySaved }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [showTextInput, setShowTextInput] = useState(false);
  const [showAudioRecorder, setShowAudioRecorder] = useState(false);
  const [showRecordingScreen, setShowRecordingScreen] = useState(false);
  const [activityText, setActivityText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [transcribedText, setTranscribedText] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  
  // Edit/Delete state
  const [editingActivity, setEditingActivity] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  
  // Exit animation state
  const [isExiting, setIsExiting] = useState(false);
  
  const handleBack = () => {
    setIsExiting(true);
    setTimeout(() => {
      onBack();
    }, 300); // Match the CSS animation duration
  };

  // Fetch activities on mount and when MRN changes
  useEffect(() => {
    fetchActivities();
  }, [patientMRN]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      // Pass logged_by to filter activities by the current user
      const response = await fetch(
        `${BACKEND_URL}/patients/${patientMRN}/activities?logged_by=${encodeURIComponent(loggedInUserName)}`
      );
      const data = await response.json();
      if (data.success) {
        setActivities(data.activities);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const translateMedicalTerms = async (text: string): Promise<string> => {
    let translatedText = text;
    
    // First translate ICD-10 codes
    try {
      const icd10Response = await fetch(`${BACKEND_URL}/translate-icd10`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: translatedText })
      });
      const icd10Data = await icd10Response.json();
      if (icd10Data.success && icd10Data.has_translations) {
        translatedText = icd10Data.translated_text;
      }
    } catch (error) {
      console.error('Error translating ICD-10 codes:', error);
    }
    
    // Then translate medical abbreviations
    try {
      const abbrevResponse = await fetch(`${BACKEND_URL}/translate-abbreviations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: translatedText })
      });
      const abbrevData = await abbrevResponse.json();
      if (abbrevData.success && abbrevData.has_translations) {
        translatedText = abbrevData.translated_text;
      }
    } catch (error) {
      console.error('Error translating medical abbreviations:', error);
    }
    
    return translatedText;
  };

  const saveActivity = async (text: string, type: 'text' | 'audio') => {
    try {
      setSaving(true);
      
      // Translate any ICD-10 codes and medical abbreviations before saving
      const translatedText = await translateMedicalTerms(text);
      
      const response = await fetch(`${BACKEND_URL}/patients/${patientMRN}/activities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          activity_text: translatedText,
          activity_type: type,
          logged_by: loggedInUserName
        })
      });
      
      const data = await response.json();
      if (data.success) {
        // Add new activity to the top of the list (newest first)
        setActivities(prev => [data.activity, ...prev]);
        // Notify parent that activity was saved (include text for report sync)
        onActivitySaved?.(type, translatedText);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving activity:', error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTextActivity = async () => {
    if (!activityText.trim()) return;
    
    const success = await saveActivity(activityText.trim(), 'text');
    if (success) {
      setActivityText('');
      setShowTextInput(false);
    }
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/wav' });
        setAudioBlob(blob);
        setShowRecordingScreen(false);
        setShowAudioRecorder(true);
        // Transcribe the recorded audio
        transcribeAudio(blob);
      };
      
      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
      setShowRecordingScreen(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      // RecordingScreen will be hidden in the recorder.onstop callback
    }
  }, [mediaRecorder, isRecording]);

  const transcribeAudio = async (blob: Blob) => {
    setIsTranscribing(true);
    const formData = new FormData();
    formData.append("audio", blob);
    
    try {
      const response = await fetch(`${BACKEND_URL}/audio/transcribe`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      if (data.success) {
        const transcript = data.translation || data.transcription || "";
        setTranscribedText(transcript);
      } else {
        console.error('Transcription failed:', data.error);
        setTranscribedText("Transcription failed. Please try again.");
      }
    } catch (error) {
      console.error('Transcription error:', error);
      setTranscribedText("Audio transcription error. Please check your connection.");
    }
    
    setIsTranscribing(false);
  };

  const handleSaveAudioActivity = async () => {
    if (!transcribedText.trim()) return;
    
    const success = await saveActivity(transcribedText.trim(), 'audio');
    if (success) {
      setTranscribedText('');
      setAudioBlob(null);
      setShowAudioRecorder(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    // Timestamps from backend are already in Central Time - display as-is without timezone conversion
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const resetAudioRecorder = () => {
    setTranscribedText('');
    setAudioBlob(null);
    setIsRecording(false);
    setIsTranscribing(false);
  };

  // Edit activity handler
  const handleEditActivity = async (timestamp: string) => {
    if (!editText.trim()) return;
    
    setSaving(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/patients/${patientMRN}/activities/${encodeURIComponent(timestamp)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            activity_text: editText.trim(),
            logged_by: loggedInUserName
          })
        }
      );
      
      const data = await response.json();
      if (data.success) {
        // Update the activity in local state
        setActivities(prev => prev.map(a => 
          a.timestamp === timestamp ? { ...a, activity_text: editText.trim() } : a
        ));
        setEditingActivity(null);
        setEditText('');
      } else {
        alert(data.message || 'Failed to update activity');
      }
    } catch (error) {
      console.error('Error updating activity:', error);
      alert('Failed to update activity. Please try again.');
    }
    setSaving(false);
  };

  // Delete activity handler
  const handleDeleteActivity = async (timestamp: string) => {
    setSaving(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/patients/${patientMRN}/activities/${encodeURIComponent(timestamp)}?logged_by=${encodeURIComponent(loggedInUserName)}`,
        { method: 'DELETE' }
      );
      
      const data = await response.json();
      if (data.success) {
        // Remove the activity from local state
        setActivities(prev => prev.filter(a => a.timestamp !== timestamp));
        setDeleteConfirm(null);
      } else {
        alert(data.message || 'Failed to delete activity');
      }
    } catch (error) {
      console.error('Error deleting activity:', error);
      alert('Failed to delete activity. Please try again.');
    }
    setSaving(false);
  };

  // Start editing an activity
  const startEditing = (activity: Activity) => {
    setEditingActivity(activity.timestamp);
    setEditText(activity.activity_text);
    setDeleteConfirm(null);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingActivity(null);
    setEditText('');
  };

  return (
    <div className={`activity-panel ${isExiting ? 'exiting' : ''}`}>
      <div className="activity-panel-header">
        <button className="back-btn" onClick={handleBack} disabled={isExiting}>
          <span>←</span>
          <span>Back</span>
        </button>
        <div className="header-content">
          <h3> Patient Activity Panel</h3>
          <p className="activity-patient-info">Recording activities for <strong>{patientName}</strong></p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="activity-action-buttons">
        <button
          className={`activity-btn type-btn ${showTextInput ? 'active' : ''}`}
          onClick={() => {
            setShowTextInput(!showTextInput);
            setShowAudioRecorder(false);
            resetAudioRecorder();
          }}
        >
          <span className="btn-icon"></span>
          <span>Type New Activity</span>
        </button>
        <button
          className={`activity-btn record-btn ${isRecording ? 'active' : ''}`}
          onClick={() => {
            if (isRecording) {
              stopRecording();
            } else {
              startRecording();
            }
            setShowTextInput(false);
            setActivityText('');
          }}
        >
          <span className="btn-icon"></span>
          <span>{isRecording ? 'Stop Recording' : 'Record Activity'}</span>
        </button>
      </div>

      {/* Text Input Section */}
      {showTextInput && (
        <div className="activity-input-section text-input-section">
          <h4>Type Activity</h4>
          <textarea
            value={activityText}
            onChange={(e) => setActivityText(e.target.value)}
            placeholder="Describe the patient activity..."
            rows={4}
            className="activity-textarea"
          />
          <div className="input-actions">
            <button 
              className="cancel-btn"
              onClick={() => {
                setShowTextInput(false);
                setActivityText('');
              }}
            >
              Cancel
            </button>
            <button
              className="save-btn"
              onClick={handleSaveTextActivity}
              disabled={!activityText.trim() || saving}
            >
              {saving ? ' Saving...' : ' Save Activity'}
            </button>
          </div>
        </div>
      )}

      {/* Audio Transcription Section - Shows after recording */}
      {showAudioRecorder && (
        <div className="activity-input-section audio-input-section">
          <h4>Recorded Activity</h4>
          
          {isTranscribing && (
            <div className="transcribing-indicator">
              <div className="transcribe-spinner"></div>
              <span>Transcribing audio...</span>
            </div>
          )}

          {transcribedText && (
            <div className="transcription-result">
              <label>Transcribed Text:</label>
              <textarea
                value={transcribedText}
                onChange={(e) => setTranscribedText(e.target.value)}
                rows={3}
                className="activity-textarea"
              />
              <div className="input-actions">
                <button 
                  className="cancel-btn"
                  onClick={() => {
                    setShowAudioRecorder(false);
                    resetAudioRecorder();
                  }}
                >
                  Cancel
                </button>
                <button className="retry-btn" onClick={resetAudioRecorder}>
                  Re-record
                </button>
                <button
                  className="save-btn"
                  onClick={handleSaveAudioActivity}
                  disabled={!transcribedText.trim() || saving}
                >
                  {saving ? 'Saving...' : 'Save Activity'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Activity List */}
      <div className="activity-list-section">
        <h4>Activity History</h4>
        {loading ? (
          <div className="activity-loading">Loading activities...</div>
        ) : activities.length === 0 ? (
          <div className="no-activities">
            <NurseClipboardIllustration />
            <p className="empty-title">No activities recorded yet</p>
            <p className="empty-hint">Use the buttons above to add activities for this patient.</p>
          </div>
        ) : (
          <div className="activity-list">
            {activities.map((activity, index) => (
              <div key={`${activity.timestamp}-${index}`} className="activity-item">
                <div className="activity-header">
                  <span className={`activity-type-badge ${activity.activity_type}`}>
                    {activity.activity_type === 'audio' ? '🎙️ Audio' : '✏️ Text'}
                  </span>
                  <span className="activity-timestamp">
                    {formatTimestamp(activity.timestamp)}
                  </span>
                </div>
                
                {/* Edit mode */}
                {editingActivity === activity.timestamp ? (
                  <div className="activity-edit-section">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="activity-edit-textarea"
                      rows={3}
                    />
                    <div className="activity-edit-actions">
                      <button 
                        className="activity-cancel-btn"
                        onClick={cancelEditing}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button 
                        className="activity-save-btn"
                        onClick={() => handleEditActivity(activity.timestamp)}
                        disabled={!editText.trim() || saving}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : deleteConfirm === activity.timestamp ? (
                  /* Delete confirmation */
                  <div className="activity-delete-confirm">
                    <p>Are you sure you want to delete this activity?</p>
                    <div className="activity-delete-actions">
                      <button 
                        className="activity-cancel-btn"
                        onClick={() => setDeleteConfirm(null)}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button 
                        className="activity-delete-btn"
                        onClick={() => handleDeleteActivity(activity.timestamp)}
                        disabled={saving}
                      >
                        {saving ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Normal view */
                  <>
                    <p className="activity-content">{activity.activity_text}</p>
                    <div className="activity-actions">
                      <button 
                        className="activity-edit-btn"
                        onClick={() => startEditing(activity)}
                        title="Edit activity"
                      >
                        Edit
                      </button>
                      <button 
                        className="activity-delete-btn"
                        onClick={() => setDeleteConfirm(activity.timestamp)}
                        title="Delete activity"
                      >
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recording Screen Overlay */}
      {showRecordingScreen && (
        <RecordingScreen onStop={stopRecording} />
      )}
    </div>
  );
};

export default PatientActivityPanel;
